import React from 'react';
import { cn } from '../../utils/cn.ts';

import { GlassRegular } from './GlassSurface/GlassRegular.tsx';
import { Icon } from './Icon.tsx';

const NativeButton = 'button';

interface SidebarTabConfig {
    id: string;
    label: string;
    icon: string;
    description?: string;
    /** Optional notification badge count (hidden when 0 or undefined) */
    badge?: number;
}

interface SidebarTabsProps {
    tabs: SidebarTabConfig[];
    activeTab: string;
    onChange: (tabId: string) => void;
    showDescriptions?: boolean;
    className?: string;
    /** Use "plain" when the parent surface already supplies glass. */
    surface?: 'glass' | 'plain';
    /** Show all toggle configuration */
    showAllToggle?: {
        /** Current state of show all */
        isShowingAll: boolean;
        /** Callback when toggle is clicked */
        onToggle: () => void;
        /** Label when collapsed (default: "View All") */
        expandLabel?: string;
        /** Label when expanded (default: "Show Less") */
        collapseLabel?: string;
    };
}

export const SidebarTabs: React.FC<SidebarTabsProps> = ({
    tabs,
    activeTab,
    onChange,
    showDescriptions = false,
    className,
    surface = 'glass',
    showAllToggle
}) => {
    const tabItems = (
        <>
            {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                return (
                    <NativeButton
                        type="button"
                        key={tab.id}
                        onClick={() => onChange(tab.id)}
                        className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                            isActive
                                ? "border border-primary bg-primary/10 text-primary"
                                : "border border-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                        )}
                    >
                        <Icon 
                            name={tab.icon}
                            size="lg"
                            className={cn(
                                "shrink-0",
                                isActive ? "text-primary" : "text-gray-400"
                            )}
                        />
                        {showDescriptions && tab.description ? (
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm">{tab.label}</div>
                                <div className="text-[10px] text-gray-500 leading-tight">{tab.description}</div>
                            </div>
                        ) : (
                            <span className="flex-1 font-medium text-sm">{tab.label}</span>
                        )}
                        {tab.badge != null && tab.badge > 0 && (
                            <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 font-bold text-[10px] text-white">
                                {tab.badge}
                            </span>
                        )}
                    </NativeButton>
                );
            })}
            {/* Show All Toggle */}
            {showAllToggle ? <NativeButton
                    type="button"
                    onClick={showAllToggle.onToggle}
                    className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg border border-gray-300 border-dashed py-2 font-medium text-primary text-xs transition-colors hover:bg-gray-50 hover:text-primary-dark dark:border-gray-600 dark:hover:bg-gray-800 dark:hover:text-primary-light"
                >
                    <Icon name={showAllToggle.isShowingAll ? 'filter_list' : 'apps'} size="xs" />
                    {showAllToggle.isShowingAll 
                        ? (showAllToggle.collapseLabel || 'Show Less')
                        : (showAllToggle.expandLabel || 'View All')
                    }
                </NativeButton> : null}
        </>
    );

    const navClassName = cn(
        "flex flex-col gap-1",
        surface === 'glass' && "p-2",
        className
    );

    if (surface === 'plain') {
        return (
            <nav className={navClassName}>
                {tabItems}
            </nav>
        );
    }

    return (
        <GlassRegular as="nav" className={navClassName}>
            {tabItems}
        </GlassRegular>
    );
};














